import classNames from "classnames";
import Icon from "~/components/shared/Icon";
import { AnnotationBody } from "~/components/build/Show/components/annotations/AnnotationBody";
import type { AnnotationStyle } from "~/components/build/Show/lib/types";

const ANNOTATION_STYLES: Record<
  AnnotationStyle,
  {
    annotationClass: string;
    icon: string;
  }
> = {
  DEFAULT: {
    annotationClass: "build-annotation-default",
    icon: "heroicons/outline/pencil",
  },
  SUCCESS: {
    annotationClass: "build-annotation-success",
    icon: "check-large",
  },
  ERROR: {
    annotationClass: "build-annotation-error",
    icon: "heroicons/16/solid/x-mark",
  },
  INFO: {
    annotationClass: "build-annotation-info",
    icon: "heroicons/outline/information-circle",
  },
  WARNING: {
    annotationClass: "build-annotation-warning",
    icon: "heroicons/16/solid/exclamation-triangle",
  },
};

type Props = {
  annotation: {
    /**
     * Stable per-annotation identifier (uuid in the SPA path, GraphQL global id in the
     * legacy Relay path). Used to namespace per-element state stored in
     * `useAnnotationElementStateStore` so e.g. `<details>` open state survives re-render.
     */
    id?: string;
    context: string;
    style: AnnotationStyle;
    body: {
      html: string | null | undefined;
    };
  };
};

export default function Annnotation({ annotation }: Props) {
  const annotationDomId = `annotation-${annotation.context}`;
  // Fall back to context if no id was threaded through (legacy callers / fixtures).
  // Context is unique within a build, which is the scope per-page-load state cares about.
  const annotationStateId = annotation.id ?? annotation.context;

  const styles = ANNOTATION_STYLES[annotation.style];

  return (
    <div
      id={annotationDomId}
      className={classNames(
        `build-annotation mb-2 flex items-stretch rounded-sm border bg-white`,
        styles?.annotationClass,
      )}
    >
      <a
        href={`#${annotationDomId}`}
        className="annotation-link flex shrink-0 items-start border-r py-4"
      >
        <Icon icon={styles?.icon} className="annotation-icon mt-[2px] h-4 w-4 min-w-0 flex-auto" />
      </a>
      <div className="min-w-0 flex-auto">
        <AnnotationBody annotationId={annotationStateId} html={annotation.body?.html} />
      </div>
    </div>
  );
}
